import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { isAdmin } from '../../auth/admins';
import { useEffectiveUser } from '../../auth/useAuthUser';
import {
  useAcceptField,
  useAggregateNow,
  useDismissDeal,
  useDismissField,
  useEditField,
  useLogDeal,
  useLogPass,
  useMergeDealInto,
  useRestoreField,
  useSetCustomInstructions,
  useSetDealRecentUpdates,
  useSuppressRecentUpdatesBlock,
  type LogResponse,
} from '../../hooks/useDeals';
import type {
  DealDetailPayload,
  DealDismissReason,
  EditHistoryEntry,
  FieldDismissReason,
  ProposalDriftConflict,
  RecentUpdates,
} from '../../types';
import { ApiError } from '../../api/client';
import { affinityOpportunityUrl } from '../../utils/affinityUrls';
import { useFieldOptions } from '../../hooks/useDealsFieldOptions';
import { useDealsFieldLayout } from '../../hooks/useDealsFieldLayout';
import { useMaterialityFilter } from '../../hooks/useMaterialityFilter';
import { AnnotationTooltip } from './DealsPage.annotationTooltip';
import { ChangeIndicator } from './DealsPage.changeIndicator';
import { dealActionsEnabled } from './DealsPage.actionState';
import { classifyChange, type ChangeKind } from './DealsPage.changeKind';
import {
  ContactRowEditor,
  type Contact as StructuredContact,
} from './DealsPage.ContactRowEditor';
import { FieldDropdown } from './DealsPage.fieldDropdown';
import { TeamChipPicker } from './DealsPage.TeamChipPicker';
import {
  DEALS_FIELD_ORDER,
  getFieldControl,
  getFieldDef,
  getFieldLabel,
} from './DealsPage.fields';
import { MaterialityControl } from './DealsPage.materialityControl';
import { DismissReasonModal } from './DismissReasonModal';
import {
  type RenderField,
  badgeStyleFor,
  coerceValue,
  emailToShortName,
  formatEtFull,
  formatLocalHHMM,
  formatValue,
  isDriftDetail,
  proposalToRenderField,
  resolvedToRenderField,
  stringValue,
} from './DealsPage.shared';
import { LogPassModal, MergeIntoDealModal } from './DealsPage.modals';
import { PushSuccessPanel } from './DealsPage.PushSuccessPanel';
import { VisibleToRow } from './DealsPage.DealListRow';
import { EvidenceRow } from './DealsPage.EvidenceRow';
import styles from './DealsPage.module.css';

/**
 * DealDetail card cluster (detail panel + field rows + custom instructions +
 * recent-updates editor + tooltips), extracted verbatim from DealsPage.tsx
 * (D4 componentization, zero behavior change). Only DealDetail is exported;
 * the field-row / recent-updates internals stay file-local.
 */

// Queue-only Phase B pending chip (owner ruling 2026-08-20). `phase_b_job`
// non-null means a real queue row exists for this deal; `needs_aggregation`
// alone (no job) covers the "owed, not queued yet" state — e.g. the daily
// cron will pick it up — and keeps the legacy label so that case isn't
// silently dropped.
function phaseBChipLabel(job: DealDetailPayload['phase_b_job']): string {
  if (!job) return '';
  if (job.status === 'running') return '⟳ Suggestions refreshing…';
  const hhmm = formatLocalHHMM(job.queued_at);
  return hhmm
    ? `⟳ Suggestions refresh pending (queued ${hhmm})`
    : '⟳ Suggestions refresh pending';
}

function phaseBChipTitle(job: DealDetailPayload['phase_b_job']): string {
  if (!job) return '';
  return job.status === 'running'
    ? 'The scheduler process is re-aggregating this deal right now.'
    : 'Queued for the scheduler process — this card will refresh automatically.';
}

export function DealDetail({
  payload,
  onNextPending,
  onBackToPipeline,
  backLabel = 'Back to Pipeline',
}: {
  payload: DealDetailPayload;
  onNextPending: (currentKey: string) => void | Promise<void>;
  onBackToPipeline: () => void;
  backLabel?: string;
}) {
  // "Why" / Insights deep-links are admin-only (the target is admin-gated);
  // gated on the EFFECTIVE user so view-as sees what that user would.
  const { effectiveEmail } = useEffectiveUser();
  const canSeeWhy = isAdmin(effectiveEmail);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [logConfirmOpen, setLogConfirmOpen] = useState(false);
  const [logPassOpen, setLogPassOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Wave A #24b — push-drift conflicts surfaced from a 409 response.
  // When set, the inline confirmation modal blocks the push action and
  // offers re-aggregate / try-anyway choices.
  const [pushDriftConflicts, setPushDriftConflicts] = useState<
    ProposalDriftConflict[] | null
  >(null);
  // Wave C #18 — field_errors from a successful (200 with partial
  // failure) push. Map of field_name -> Affinity error message.
  // Cleared on next push attempt.
  const [postPushFieldErrors, setPostPushFieldErrors] = useState<
    Record<string, string> | null
  >(null);
  const [logSuccess, setLogSuccess] = useState<LogResponse | null>(null);
  // Queue-only Phase B (owner ruling 2026-08-20) — these mutations now enqueue
  // rather than run inline, so the operator needs telling that the "nothing
  // happened" button actually queued real work.
  const [aggregateNowNote, setAggregateNowNote] = useState<string | null>(null);
  const [mergeSuccessNote, setMergeSuccessNote] = useState<string | null>(null);

  const dismissDeal = useDismissDeal();
  const logDealMut = useLogDeal();
  const logPassMut = useLogPass();
  const aggregateNowMut = useAggregateNow();
  const mergeIntoMut = useMergeDealInto();
  const [mergeIntoOpen, setMergeIntoOpen] = useState(false);

  const runAggregateNow = () => {
    setAggregateNowNote(null);
    aggregateNowMut.mutate(
      { dealKey: payload.deal_key },
      {
        onSuccess: () => setAggregateNowNote('Suggestions will refresh shortly.'),
        onError: (e) => setActionError(String(e)),
      },
    );
  };

  const badgeClass =
    payload.badge === 'NEW_DEAL' ? styles.badgeNewDeal : styles.badgeFieldUpdate;
  const isCandidate = payload.deal_kind === 'candidate';
  const logLabel = isCandidate ? 'Log Deal' : 'Push Updates';
  const affinityOppId =
    payload.affinity_opp_id ??
    (payload.deal_kind === 'affinity_opp' && /^\d+$/.test(payload.deal_key)
      ? Number(payload.deal_key)
      : null);
  // Whether the Log/Push/Dismiss/Merge action bar shows — see dealActionsEnabled
  // (DealsPage.actionState.ts) for the full rule incl. the DU-1 open-candidate
  // clause. Pulled out as a pure, unit-tested helper (pre-extraction for D4).
  const unpushedCount = payload.unpushed_field_count;
  const proposalActionsEnabled = dealActionsEnabled(payload);

  // Materiality filter. Defaults to 'all' for both NEW DEAL and UPDATE
  // cards — the backend's layout-fill pass populates the full configured
  // field set on every card, so operators see the same comprehensive
  // grid on both. Operator can still toggle to 'changes' / 'material'
  // for the narrower views. Per spec:
  // docs/archive/pre-cut-2026-08/update-card-show-all-fields-spec.md
  const defaultMaterialityMode = 'all';
  const [materialityMode, setMaterialityMode] = useMaterialityFilter(
    defaultMaterialityMode,
  );

  // Field-grid ordering — prefer the admin-editable layout from the
  // backend (`useDealsFieldLayout`); fall back to the hardcoded
  // DEALS_FIELD_ORDER when the table is empty / fetch is in flight.
  // Hidden rows (visible=false) are excluded from the order; orphan
  // payload fields (not in the canonical list) render at the tail.
  const fieldLayoutQuery = useDealsFieldLayout();
  const dynamicFieldOrder = useMemo(() => {
    const rows = fieldLayoutQuery.data?.fields;
    if (!rows || rows.length === 0) return null;
    return rows
      .filter((r) => r.visible)
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map((r) => r.field_name);
  }, [fieldLayoutQuery.data]);

  // Review token (spec §10.4): the suggestion id this card was rendered from,
  // echoed on every mutation so the server 409s if the card went stale.
  const reviewSuggestionId = useMemo(() => {
    const fs = payload.field_states ?? [];
    for (const s of fs) {
      if (s.suggestion_id != null) return s.suggestion_id;
    }
    return null;
  }, [payload.field_states]);

  // Render rows from the resolver's field_states when present (so dismissed
  // rows stay visible with the right status); fall back to the legacy `fields`
  // list for older backends / stale bundles (spec §10.2, §11.1).
  const renderFields: RenderField[] = useMemo(() => {
    if (payload.field_states && payload.field_states.length > 0) {
      return payload.field_states.map(resolvedToRenderField);
    }
    return payload.fields.map(proposalToRenderField);
  }, [payload.field_states, payload.fields]);

  const orderedFields = useMemo(() => {
    const order = dynamicFieldOrder ?? DEALS_FIELD_ORDER;
    const knownSet = new Set(order);
    const byName = new Map(renderFields.map((f) => [f.field_name, f]));
    const inOrder: RenderField[] = [];
    for (const name of order) {
      const f = byName.get(name);
      if (f) inOrder.push(f);
    }
    const orphans = renderFields.filter((f) => !knownSet.has(f.field_name));
    if (orphans.length > 0) {
      console.warn(
        'DealsPage: unknown fields in payload (rendered at tail):',
        orphans.map((o) => o.field_name),
      );
    }
    return [...inOrder, ...orphans];
  }, [renderFields, dynamicFieldOrder]);

  const hasNonMaterialProposals = useMemo(
    () => orderedFields.some((f) => f.materiality === 'not_material'),
    [orderedFields],
  );

  const visibleFields = useMemo(() => {
    if (materialityMode === 'all') return orderedFields;
    // Changes/Material filtering is deferred for this build (spec §11.4): the
    // filter must NEVER hide a write-eligible or dismissed row. It only folds
    // away truly-unchanged rows.
    return orderedFields.filter((f) => f.status !== 'unchanged');
  }, [orderedFields, materialityMode]);

  // Push/Log confirmation lists exactly what will sync (spec §12.3.3). Driven
  // by the resolver's write_eligible flag, so dismissed/unchanged rows are
  // excluded and the modal can't imply a dismissed suggestion will write.
  const writeEligibleFields = useMemo(
    () => orderedFields.filter((f) => f.write_eligible),
    [orderedFields],
  );
  const dismissedSuggestionFields = useMemo(
    () => orderedFields.filter((f) => f.status === 'dismissed'),
    [orderedFields],
  );

  if (logSuccess) {
    return (
      <>
        <div className={styles.detailHeader}>
          <span className={`${styles.badge} ${badgeClass}`} style={badgeStyleFor(payload.badge)}>
            {payload.badge === 'NEW_DEAL' ? 'New Deal' : 'Update'}
          </span>
          <h3 className={styles.detailTitle}>{payload.display_name}</h3>
          {logSuccess.affinity_opp_id != null && (
            <a
              href={affinityOpportunityUrl(logSuccess.affinity_opp_id)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.affinityCardLink}
            >
              Open in Affinity
            </a>
          )}
        </div>
        <PushSuccessPanel
          actionLabel={isCandidate ? 'Deal logged' : 'Update pushed'}
          result={logSuccess}
          onNextPending={() => onNextPending(payload.deal_key)}
          onBackToPipeline={onBackToPipeline}
          backLabel={backLabel}
        />
      </>
    );
  }

  return (
    <>
      <div className={styles.detailHeader}>
        <span className={`${styles.badge} ${badgeClass}`} style={badgeStyleFor(payload.badge)}>
          {payload.badge === 'NEW_DEAL' ? 'New Deal' : 'Update'}
        </span>
        <h3 className={styles.detailTitle}>{payload.display_name}</h3>
        {payload.phase_b_job ? (
          <span className={styles.aggregatingChip} title={phaseBChipTitle(payload.phase_b_job)}>
            {phaseBChipLabel(payload.phase_b_job)}
          </span>
        ) : payload.needs_aggregation ? (
          <span
            className={styles.aggregatingChip}
            title="Re-aggregating after a recent change — this card will refresh automatically"
          >
            ⟳ aggregating…
          </span>
        ) : null}
        {affinityOppId != null && (
          <a
            href={affinityOpportunityUrl(affinityOppId)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.affinityCardLink}
          >
            Open in Affinity
          </a>
        )}
      </div>
      {payload.visible_to_meta.length > 0 && (
        <VisibleToRow
          entries={payload.visible_to_meta}
          dealKey={payload.deal_key}
          dealKind={payload.deal_kind}
        />
      )}

      {proposalActionsEnabled ? (
      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => {
            setActionError(null);
            setLogConfirmOpen(true);
          }}
          disabled={logDealMut.isPending}
        >
          {logLabel}{!isCandidate && unpushedCount > 0 ? ` (${unpushedCount})` : ''}
        </button>
        <button
          type="button"
          className={styles.btnLogPass}
          onClick={() => {
            setActionError(null);
            setLogPassOpen(true);
          }}
          disabled={logPassMut.isPending}
        >
          Log Pass
        </button>
        <button
          type="button"
          className={styles.btnDanger}
          onClick={() => setDismissOpen(true)}
          disabled={dismissDeal.isPending}
        >
          Dismiss
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => setMergeIntoOpen(true)}
          disabled={mergeIntoMut.isPending}
          title="This deal is the same as a different deal already in our system. Rebind this deal's events to the other one and dismiss this card."
        >
          ⇄ Merge
        </button>
        <a
          href={`/?tab=pipeline-insights&deal_kind=${payload.deal_kind}&deal_key=${payload.deal_key}`}
          className={styles.btnSecondary}
          style={{ textDecoration: 'none', marginLeft: 'auto' }}
          title="Open this deal in Pipeline Insights → Investigate"
        >
          🔍 Trace
        </a>
      </div>
      ) : (
        <div className={styles.completedCardNotice}>
          {/* DU-1: never claim "in sync with Affinity" for a candidate (it was
              never in Affinity). With the actionability fix above an OPEN
              candidate no longer reaches this branch; this guards the promoted/
              dismissed/merged tail + any future caller. */}
          {isCandidate
            ? 'No changes to review on this candidate.'
            : 'No unpushed changes — this card is in sync with Affinity.'}
          {affinityOppId != null && (
            <a
              href={affinityOpportunityUrl(affinityOppId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Affinity record
            </a>
          )}
        </div>
      )}

      {actionError && <div className={styles.errorBox}>{actionError}</div>}

      {mergeSuccessNote && !mergeIntoMut.isPending && (
        <div className={styles.completedCardNotice}>{mergeSuccessNote}</div>
      )}

      {aggregateNowNote && !aggregateNowMut.isPending && (
        <div className={styles.completedCardNotice}>{aggregateNowNote}</div>
      )}

      {postPushFieldErrors && Object.keys(postPushFieldErrors).length > 0 && (
        <div className={styles.driftBanner}>
          <div className={styles.driftBannerHeader}>
            ⚠ Push partially failed — {Object.keys(postPushFieldErrors).length} field(s)
            {' '}didn't write to Affinity. Other fields landed; try again on the failures.
          </div>
          <ul className={styles.driftBannerFields}>
            {Object.entries(postPushFieldErrors).map(([field, err]) => (
              <li key={field}>
                <strong>{field}</strong>: {err}
              </li>
            ))}
          </ul>
          <div className={styles.driftBannerActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setPostPushFieldErrors(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* The yellow "Affinity has newer data" banner lived here (Wave A #24a), fed by the
          `proposal_drift` key. Deleted with `services/deal_live_compare.py` by the Pillar-D write
          funeral, 2026-08-27: its flag shipped False and was never flipped, and the warning stops
          meaning anything once the subscription ends on 2026-09-30. crm-of-record-plan §5. */}

      {pushDriftConflicts && pushDriftConflicts.length > 0 && (
        <div className={styles.driftBanner}>
          <div className={styles.driftBannerHeader}>
            ⚠ Push blocked — Affinity changed since you reviewed.
            {' '}
            {pushDriftConflicts.length} field(s) differ from the snapshot
            you approved.
          </div>
          <ul className={styles.driftBannerFields}>
            {pushDriftConflicts.map((c) => (
              <li key={c.field_name}>
                <strong>{c.field_name}</strong>: you approved{' '}
                {JSON.stringify(c.proposed_value)}; Affinity now has{' '}
                {JSON.stringify(c.live_value)} (was{' '}
                {JSON.stringify(c.snapshot_value)})
              </li>
            ))}
          </ul>
          <div className={styles.driftBannerActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setPushDriftConflicts(null);
                runAggregateNow();
              }}
              disabled={aggregateNowMut.isPending}
            >
              {aggregateNowMut.isPending ? 'Queuing…' : 'Re-aggregate'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setPushDriftConflicts(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {(payload.fields.length > 0 || payload.recent_updates) && (
        <div className={styles.section}>
          <div className={styles.fieldGridHeader}>
            <h4 className={styles.sectionTitle}>
              {payload.badge === 'NEW_DEAL' ? 'Proposed fields' : 'Proposed changes'}
            </h4>
            <MaterialityControl
              value={materialityMode}
              onChange={setMaterialityMode}
              hasNonMaterialProposals={hasNonMaterialProposals}
              className={styles.materialityBar}
              buttonClassName={styles.materialityBtn}
              buttonActiveClassName={styles.materialityBtnActive}
            />
          </div>
          {visibleFields.length === 0 && !payload.recent_updates ? (
            <div className={styles.fieldGridEmpty}>
              {materialityMode === 'material'
                ? 'No material changes on this card. Switch to Changes or All.'
                : materialityMode === 'changes'
                ? 'No fields have changed since last log. Switch to All to view current values.'
                : 'No fields proposed.'}
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              {(() => {
                // Recent Updates sits in the narrative cluster: between
                // opportunity_description and next_steps. Insert before
                // next_steps; else after opportunity_description; else at the
                // end (so it's always visible regardless of the materiality
                // filter).
                const nsIdx = visibleFields.findIndex(
                  (f) => f.field_name === 'next_steps',
                );
                const odIdx = visibleFields.findIndex(
                  (f) => f.field_name === 'opportunity_description',
                );
                const insertAt =
                  nsIdx >= 0 ? nsIdx : odIdx >= 0 ? odIdx + 1 : visibleFields.length;
                const ru = payload.recent_updates ? (
                  <RecentUpdatesEditor
                    key="__recent_updates"
                    dealKey={payload.deal_key}
                    dealKind={payload.deal_kind}
                    recentUpdates={payload.recent_updates}
                    reviewSuggestionId={reviewSuggestionId}
                    canSeeWhy={canSeeWhy}
                  />
                ) : null;
                const rows = visibleFields.flatMap((f, i) => {
                  const row = (
                    <FieldRow
                      key={f.field_name}
                      dealKey={payload.deal_key}
                      dealKind={payload.deal_kind}
                      field={f}
                      reviewSuggestionId={reviewSuggestionId}
                      actionsDisabled={!proposalActionsEnabled}
                      canSeeWhy={canSeeWhy}
                    />
                  );
                  return ru && i === insertAt ? [ru, row] : [row];
                });
                if (ru && insertAt >= visibleFields.length) rows.push(ru);
                return rows;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Deal Team field — moved into the FieldProposal grid above
         (PR 1 of deal-team-field-fix-spec, 2026-05-28). The legacy
         hardcoded side-section that lived here is gone; the
         deal_team_emails row appears at position #3 / zone=team per
         deals_field_layout, editable via TeamChipPicker. */}

      {payload.evidence_visible.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>
            Evidence ({payload.evidence_visible.length} visible
            {payload.evidence_hidden_count > 0 &&
              ` · ${payload.evidence_hidden_count} hidden from you`})
          </h4>
          <div className={styles.evidenceGrid}>
            <div className={`${styles.evidenceGridHeader} ${styles.evidenceGridRow}`}>
              <span></span>
              <span>Subject</span>
              <span>From</span>
              <span>To</span>
              <span>Time</span>
              <span></span>
            </div>
            {payload.evidence_visible.slice(0, 25).map((ev) => (
              <EvidenceRow
                key={ev.event_log_id}
                ev={ev}
                dealKey={payload.deal_key}
              />
            ))}
          </div>
        </div>
      )}

      {payload.evidence_visible.length === 0 && payload.evidence_hidden_count > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Evidence</h4>
          <div className={styles.evidenceHidden}>
            {payload.evidence_hidden_count} event(s) not visible to you
          </div>
        </div>
      )}

      <CustomInstructionsEditor
        dealKey={payload.deal_key}
        initial={payload.custom_instructions ?? ''}
      />

      {/* The raw-HTML ProposedNoteEditor is retired — the Recent Updates
          two-block editor (above) + the next_steps field cover the note. */}

      {payload.last_aggregated_at && (
        <div className={styles.detailMeta} style={{ marginTop: 16 }}>
          Last aggregated {formatEtFull(payload.last_aggregated_at)}
          {payload.aggregator_model && ` · ${payload.aggregator_model}`}
          {payload.aggregator_cost_usd != null &&
            ` · $${payload.aggregator_cost_usd.toFixed(3)}`}
        </div>
      )}

      {(payload.last_edited_at || payload.last_pushed_at) && (
        <div className={styles.detailMeta} style={{ marginTop: 4 }}>
          {payload.last_edited_at && (
            <div>
              Last edited {formatEtFull(payload.last_edited_at)}
              {payload.last_edited_by && ` by ${emailToShortName(payload.last_edited_by)}`}
            </div>
          )}
          {payload.last_pushed_at && (
            <div>
              Last pushed {formatEtFull(payload.last_pushed_at)}
              {payload.last_pushed_by && ` by ${emailToShortName(payload.last_pushed_by)}`}
            </div>
          )}
        </div>
      )}

      <DismissReasonModal
        open={dismissOpen}
        granularity="deal"
        dealKind={payload.deal_kind}
        subjectLabel={payload.display_name}
        submitting={dismissDeal.isPending}
        onClose={() => setDismissOpen(false)}
        onConfirm={(reason, note) => {
          dismissDeal.mutate(
            { dealKey: payload.deal_key, reason: reason as DealDismissReason, note },
            { onSuccess: () => setDismissOpen(false) },
          );
        }}
      />

      {logConfirmOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLogConfirmOpen(false);
          }}
        >
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>{logLabel}</h3>
            <div className={styles.modalSubtitle}>{payload.display_name}</div>
            <p style={{ fontSize: 13, color: '#1A1A1A', margin: '8px 0' }}>
              {isCandidate
                ? 'About to create an Affinity opportunity and post the AI-generated summary note. Only the fields listed below will be written.'
                : `About to write ${writeEligibleFields.length} field value${writeEligibleFields.length === 1 ? '' : 's'} to Affinity and post an updated note.`}
            </p>
            {writeEligibleFields.length > 0 ? (
              <div style={{ fontSize: 13, margin: '8px 0' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Will write to Affinity:</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {writeEligibleFields.map((f) => (
                    <li key={f.field_name}>{getFieldLabel(f.field_name)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: '#8A8780', margin: '8px 0' }}>
                No field values will be written (the AI note still posts).
              </p>
            )}
            {dismissedSuggestionFields.length > 0 && (
              <div style={{ fontSize: 12, color: '#8A8780', margin: '8px 0' }}>
                Excluded (suggestion dismissed):{' '}
                {dismissedSuggestionFields.map((f) => getFieldLabel(f.field_name)).join(', ')}
              </div>
            )}
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={() => setLogConfirmOpen(false)}
                disabled={logDealMut.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={logDealMut.isPending}
                onClick={() => {
                  setActionError(null);
                  setPostPushFieldErrors(null);
                  setLogSuccess(null);
                  logDealMut.mutate(
                    { dealKey: payload.deal_key, reviewSuggestionId },
                    {
                      onSuccess: (data) => {
                        setLogConfirmOpen(false);
                        // Wave C #18 — surface partial-failure field_errors
                        // inline so the operator knows which fields didn't
                        // actually write to Affinity.
                        if (
                          data?.field_errors &&
                          Object.keys(data.field_errors).length > 0
                        ) {
                          setPostPushFieldErrors(data.field_errors);
                        }
                        setLogSuccess(data);
                      },
                      onError: (err: unknown) => {
                        if (
                          err instanceof ApiError && err.status === 409 &&
                          isDriftDetail(err.detail)
                        ) {
                          // Wave A #24b — pre-write drift detected.
                          // Close the confirm modal so the conflicts
                          // banner is visible underneath.
                          setLogConfirmOpen(false);
                          setPushDriftConflicts(err.detail.conflicts);
                          return;
                        }
                        setActionError(
                          err instanceof Error ? err.message : 'Log failed',
                        );
                      },
                    },
                  );
                }}
              >
                {logDealMut.isPending ? 'Working…' : `Confirm ${logLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {logPassOpen && (
        <LogPassModal
          subjectLabel={payload.display_name}
          submitting={logPassMut.isPending}
          onClose={() => setLogPassOpen(false)}
          onSubmit={(body) => {
            setActionError(null);
            logPassMut.mutate(
              {
                dealKey: payload.deal_key,
                note: body.note,
                category: body.category,
              },
              {
                onSuccess: () => setLogPassOpen(false),
                onError: (err: unknown) => {
                  if (
                    err instanceof ApiError && err.status === 409 &&
                    isDriftDetail(err.detail)
                  ) {
                    setLogPassOpen(false);
                    setPushDriftConflicts(err.detail.conflicts);
                    return;
                  }
                  setActionError(
                    err instanceof Error ? err.message : 'Log pass failed',
                  );
                },
              },
            );
          }}
        />
      )}

      {mergeIntoOpen && (
        <MergeIntoDealModal
          sourceLabel={payload.display_name}
          sourceDealKind={payload.deal_kind}
          sourceDealKey={payload.deal_key}
          submitting={mergeIntoMut.isPending}
          onClose={() => setMergeIntoOpen(false)}
          onSubmit={(target) => {
            setActionError(null);
            mergeIntoMut.mutate(
              {
                dealKey: payload.deal_key,
                targetDealKind: target.kind,
                targetDealKey: target.deal_key,
              },
              {
                onSuccess: (data) => {
                  setMergeIntoOpen(false);
                  // Queue-only Phase B — the merge target's re-aggregation is
                  // enqueued, not run inline, so tell the operator it's real
                  // work rather than leaving the modal's disappearance as the
                  // only signal anything happened.
                  if (data.aggregate_job_id != null) {
                    setMergeSuccessNote('Suggestions will refresh shortly.');
                  }
                },
                onError: (err: unknown) => {
                  setActionError(
                    err instanceof Error ? err.message : 'Merge failed',
                  );
                },
              },
            );
          }}
        />
      )}
    </>
  );
}

interface FieldRowProps {
  dealKey: string;
  dealKind: 'candidate' | 'affinity_opp';
  field: RenderField;
  reviewSuggestionId?: number | null;
  actionsDisabled?: boolean;
  /** Show the "why?" / Insights deep-links (admin-only). */
  canSeeWhy?: boolean;
}

function FieldRow({
  dealKey, dealKind, field, reviewSuggestionId = null, actionsDisabled = false,
  canSeeWhy = false,
}: FieldRowProps) {
  const acceptMut = useAcceptField();
  const editMut = useEditField();
  const dismissMut = useDismissField();
  const restoreMut = useRestoreField();
  const label = getFieldLabel(field.field_name);
  const isDismissed = field.status === 'dismissed';
  const isPushFailed = field.status === 'push_failed';
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(stringValue(field.proposed_value));
  // Phase 2 — dropdown-mode editing carries its own state (string for
  // single, string[] for multi). Initialized lazily on entering edit
  // mode so the value stays in sync with the proposal across reloads.
  const initialDropdownValue = (): string | string[] => {
    if (Array.isArray(field.proposed_value)) {
      return field.proposed_value.map((v) => String(v));
    }
    return field.proposed_value == null ? '' : String(field.proposed_value);
  };
  const [dropdownValue, setDropdownValue] = useState<string | string[]>(initialDropdownValue);
  const [dismissOpen, setDismissOpen] = useState(false);

  useEffect(() => {
    setEditValue(stringValue(field.proposed_value));
    setDropdownValue(initialDropdownValue());
    // initialDropdownValue is defined in this scope and only depends on
    // field.proposed_value — safe to re-run when the proposal changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.proposed_value]);

  // Phase 2 — dispatch on the field's control type. Dropdown fields use
  // the new <FieldDropdown>; everything else stays on the existing
  // free-text editor.
  const controlType = getFieldControl(field.field_name);
  const isDropdown = controlType === 'dropdown_single' || controlType === 'dropdown_multi';
  const isTeamChip = controlType === 'team_chip';
  const isContactRows = controlType === 'contact_rows';
  const isLongText =
    field.field_name === 'opportunity_description' ||
    field.field_name === 'next_steps';
  const fieldOptions = useFieldOptions(field.field_name);

  // Phase 1 — half/full-width zone metadata drives the 2-col CSS grid.
  // Orphan fields (not in DEALS_FIELD_DEFS) default to full-width.
  const fieldDef = getFieldDef(field.field_name);
  const widthClass = fieldDef?.width === 'half' ? styles.fieldRowHalf : styles.fieldRowFull;
  const changeKind: ChangeKind = classifyChange(field);
  const changeBorderClass = changeKind
    ? changeKind === 'add'
      ? styles.fieldRowChangeAdd
      : changeKind === 'remove'
      ? styles.fieldRowChangeRemove
      : styles.fieldRowChangeEdit
    : '';

  const rowClass = [
    styles.fieldRow,
    widthClass,
    changeBorderClass,
    field.user_set ? styles.fieldUserSet : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (editing && !isTeamChip && !isContactRows) {
    return (
      <div className={`${rowClass} ${styles.fieldEditing}`}>
        <div className={styles.fieldLabel}>{label}</div>
        {isDropdown ? (
          <FieldDropdown
            fieldName={field.field_name}
            value={dropdownValue}
            options={fieldOptions.options}
            mode={controlType === 'dropdown_multi' ? 'multi' : 'single'}
            onChange={setDropdownValue}
            disabled={editMut.isPending}
          />
        ) : isLongText ? (
          <textarea
            className={styles.longTextEdit}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
            rows={6}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
          />
        )}
        <div className={styles.editActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={editMut.isPending}
            onClick={() => {
              // Dropdown saves the raw value (string or array). Text
              // editor still goes through coerceValue (number / bool /
              // JSON parsing). Phase 2 keeps both code paths because
              // text inputs still need free-typing for numeric fields
              // (no `dropdown_numeric` mode).
              const payload = isDropdown
                ? dropdownValue
                : isLongText
                ? editValue
                : coerceValue(editValue);
              // No-op guard: saving the proposed value unchanged is not an
              // authored edit. Close without recording an override; use the
              // explicit Accept (✓) to accept. (Backend mirrors this — a no-op
              // edit is recorded as an acceptance, not an edit.)
              if (
                JSON.stringify(payload) === JSON.stringify(field.proposed_value)
              ) {
                setEditing(false);
                return;
              }
              editMut.mutate(
                { dealKey, fieldName: field.field_name, value: payload },
                { onSuccess: () => setEditing(false) },
              );
            }}
          >
            Save
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => {
              setEditValue(stringValue(field.proposed_value));
              setDropdownValue(initialDropdownValue());
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Dismissed-suggestion row (spec §5.5). The field stays VISIBLE and falls
  // back to the effective current/prior value; the rejected suggestion is not
  // shown as the working value. Muted treatment, no green proposal border, no
  // Accept/Dismiss — the recovery action is Restore.
  if (isDismissed) {
    const effectiveStr = formatValue(field.effective_value);
    return (
      <div className={`${styles.fieldRow} ${widthClass} ${styles.fieldRowDismissed}`}>
        <div className={styles.fieldLabel}>
          <span className={styles.fieldLabelText}>{label}</span>
          <span className={styles.fieldDismissedStatus}>
            Suggestion dismissed
            {field.dismiss_reason ? ` · ${field.dismiss_reason.replace(/_/g, ' ')}` : ''}
          </span>
        </div>
        <div
          className={styles.fieldValue}
          title={
            field.dismissed_proposed_value != null
              ? `Rejected suggestion: ${formatValue(field.dismissed_proposed_value)}`
              : undefined
          }
        >
          {effectiveStr || '—'}
        </div>
        <div className={styles.fieldActions}>
          <button
            type="button"
            className={styles.fieldActionBtn}
            title="Restore suggestion"
            aria-label={`Restore the dismissed suggestion for ${label}`}
            disabled={restoreMut.isPending || actionsDisabled}
            onClick={() => {
              restoreMut.mutate({ dealKey, fieldName: field.field_name, reviewSuggestionId });
            }}
          >
            ↺
          </button>
          {canSeeWhy && (
            <a
              href={
                `/?tab=pipeline-insights&deal_kind=${dealKind}`
                + `&deal_key=${encodeURIComponent(dealKey)}`
                + `#field-${encodeURIComponent(field.field_name)}`
              }
              className={styles.fieldActionBtn}
              title="Why this field? — open in Pipeline Insights"
              aria-label={`Why was ${label} suggested? Open in Pipeline Insights`}
              style={{ textDecoration: 'none' }}
            >
              ?
            </a>
          )}
        </div>
      </div>
    );
  }

  // Side-by-side prior → proposed when both exist (Field Update path).
  // New Deal cards have prior_value=null and just show the proposed value.
  const priorStr = formatValue(field.prior_value);
  const proposedStr = formatValue(field.proposed_value);
  const showDiff =
    field.prior_value !== null &&
    field.prior_value !== undefined &&
    priorStr !== proposedStr;

  return (
    <>
      <div className={rowClass}>
        <div className={styles.fieldLabel}>
          <span className={styles.fieldLabelText}>{label}</span>
          <ChangeIndicator
            field={field}
            indicatorClassName={styles.fieldChangeTagWrapper}
            tagClassName={styles.fieldChangeTag}
          />
          <AnnotationTooltip
            field={field}
            wrapperClassName={styles.fieldAnnotationWrapper}
            iconClassName={styles.fieldAnnotationIcon}
            popoverClassName={styles.fieldAnnotationPopover}
            rowClassName={styles.fieldAnnotationRow}
            labelClassName={styles.fieldAnnotationLabel}
          />
        </div>
        <div className={styles.fieldValue}>
          {isTeamChip ? (
            <TeamChipPicker
              value={
                Array.isArray(field.proposed_value)
                  ? (field.proposed_value as unknown[]).map((v) => String(v))
                  : []
              }
              onChange={(next) => {
                editMut.mutate({
                  dealKey, fieldName: field.field_name, value: next, reviewSuggestionId,
                });
              }}
              disabled={editMut.isPending || actionsDisabled}
            />
          ) : isContactRows ? (
            <ContactRowEditor
              value={(field.proposed_value as StructuredContact[]) || []}
              onChange={(next) => {
                editMut.mutate({
                  dealKey, fieldName: field.field_name, value: next, reviewSuggestionId,
                });
              }}
              disabled={editMut.isPending || actionsDisabled}
            />
          ) : showDiff ? (
            <span>
              <span className={styles.priorValue} title="Current Affinity value">
                {priorStr}
              </span>
              <span className={styles.diffArrow}> → </span>
              <span className={styles.proposedValue} title="Proposed value">
                {proposedStr}
              </span>
            </span>
          ) : (
            proposedStr
          )}
          {/* Visible confirmation that Accept/Edit registered (spec §5.3-5.4). */}
          {field.status === 'accepted' && (
            <span className={styles.fieldStatusBadge}> · Accepted</span>
          )}
          {field.status === 'edited' && (
            <EditHistoryTooltip
              history={field.edit_history}
              label={<span className={styles.fieldStatusBadge}> · Edited</span>}
            />
          )}
          {isPushFailed && field.push_error && (
            <div className={styles.fieldPushError}>
              Affinity rejected this field: {field.push_error}
            </div>
          )}
        </div>
        <div className={styles.fieldActions}>
          {/* Accept belongs only to an active AI proposal (spec §8 action
             table). Unchanged / edited / accepted / push_failed rows don't
             show it (§12.2.11). */}
          {field.status === 'proposed' && (
            <button
              type="button"
              className={styles.fieldActionBtn}
              title="Accept proposed value"
              aria-label={`Accept the proposed value for ${label}`}
              disabled={acceptMut.isPending || actionsDisabled}
              onClick={() => {
                acceptMut.mutate({ dealKey, fieldName: field.field_name, reviewSuggestionId });
              }}
            >
              ✓
            </button>
          )}
          {!isTeamChip && !isContactRows && (
            <button
              type="button"
              className={styles.fieldActionBtn}
              title="Edit"
              aria-label={`Edit ${label}`}
              disabled={actionsDisabled}
              onClick={() => setEditing(true)}
            >
              ✎
            </button>
          )}
          {/* Dismiss only when there is an active AI suggestion to reject:
             proposed or push_failed (spec §8 action table). Unchanged /
             edited / accepted rows have no suggestion to dismiss
             (§12.2.12). */}
          {(field.status === 'proposed' || field.status === 'push_failed') && (
            <button
              type="button"
              className={styles.fieldActionBtn}
              title="Dismiss suggestion"
              aria-label={`Dismiss the suggestion for ${label}`}
              disabled={actionsDisabled}
              onClick={() => setDismissOpen(true)}
            >
              ×
            </button>
          )}
          {/* PR 6 — Pipeline Insights deep links (admin-only; Insights is
             admin-gated, so these are dead-ends for non-admins). */}
          {canSeeWhy && (
            <a
              href={
                `/?tab=pipeline-insights&deal_kind=${dealKind}`
                + `&deal_key=${encodeURIComponent(dealKey)}`
                + `#field-${encodeURIComponent(field.field_name)}`
              }
              className={styles.fieldActionBtn}
              title="Why this field? — open in Pipeline Insights"
              style={{ textDecoration: 'none' }}
            >
              ?
            </a>
          )}
          {canSeeWhy && field.user_set && (
            <a
              href={
                `/?tab=pipeline-insights&mode=health`
                + `&cohort=field_override&field_name=${encodeURIComponent(field.field_name)}`
              }
              className={styles.fieldActionBtn}
              title={`Show similar overrides of ${field.field_name}`}
              style={{ textDecoration: 'none' }}
            >
              📈
            </a>
          )}
        </div>
      </div>
      <DismissReasonModal
        open={dismissOpen}
        granularity="field"
        subjectLabel={`${field.field_name} = ${formatValue(field.proposed_value)}`}
        submitting={dismissMut.isPending}
        onClose={() => setDismissOpen(false)}
        onConfirm={(reason, note) => {
          dismissMut.mutate(
            {
              dealKey,
              fieldName: field.field_name,
              reason: reason as FieldDismissReason,
              note,
              reviewSuggestionId,
            },
            { onSuccess: () => setDismissOpen(false) },
          );
        }}
      />
    </>
  );
}

interface CustomInstructionsProps {
  dealKey: string;
  initial: string;
}

function CustomInstructionsEditor({ dealKey, initial }: CustomInstructionsProps) {
  const [text, setText] = useState(initial);
  const [editing, setEditing] = useState(false);
  const setMut = useSetCustomInstructions();

  useEffect(() => {
    setText(initial);
  }, [initial, dealKey]);

  if (!editing) {
    return (
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Custom instructions</h4>
        {initial ? (
          <div className={styles.synthDesc}>{initial}</div>
        ) : (
          <div className={styles.evidenceHidden}>
            (none — add a directive the aggregator reads on every run)
          </div>
        )}
        <button
          type="button"
          className={styles.btnGhost}
          onClick={() => setEditing(true)}
        >
          {initial ? 'Edit' : 'Add'} instructions
        </button>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>Custom instructions</h4>
      <textarea
        className={styles.ciEditor}
        rows={4}
        maxLength={5000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. Treat banker mentions as advisory, not committed. Bias next_steps toward LOI."
      />
      <div className={styles.ciActions}>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={setMut.isPending}
          onClick={() => {
            setMut.mutate(
              { dealKey, text: text.trim() ? text : null },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          Save
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => {
            setText(initial);
            setEditing(false);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Recent Updates (Progress + New Info) — FieldRow-matching rich-text ─────

/** Generic hover/click popover reusing the field-annotation classes (the ⓘ
 * pattern), so Recent-Updates info + edit-history read identically to the
 * field ⓘ. Hover opens; click pins; outside-click closes. */
function HoverPopover({
  trigger,
  children,
}: {
  trigger: ReactNode;
  children: ReactNode;
}) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!pinned) return undefined;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pinned]);
  const open = pinned || hovered;
  return (
    <span
      ref={ref}
      className={styles.fieldAnnotationWrapper}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }}
    >
      {trigger}
      {open && (
        <span className={styles.fieldAnnotationPopover} role="tooltip">
          {children}
        </span>
      )}
    </span>
  );
}

/** Hover the "· Edited" marker → who/when history (newest-first). Renders the
 * label bare when there's no history. */
function EditHistoryTooltip({
  history,
  label,
}: {
  history?: EditHistoryEntry[];
  label: ReactNode;
}) {
  if (!history || history.length === 0) return <>{label}</>;
  return (
    <HoverPopover trigger={label}>
      {history.map((h, i) => (
        <span key={i} className={styles.fieldAnnotationRow}>
          <span className={styles.fieldAnnotationLabel}>
            {emailToShortName(h.edited_by)}
          </span>
          <span>{formatEtFull(h.edited_at)}</span>
        </span>
      ))}
    </HoverPopover>
  );
}

/** ⓘ for a Recent Updates block — Source (AI/edited) + what the block is. */
function RecentUpdatesInfoTooltip({
  source,
  what,
}: {
  source: 'user' | 'ai';
  what: string;
}) {
  return (
    <HoverPopover trigger={<span className={styles.fieldAnnotationIcon}>ⓘ</span>}>
      <span className={styles.fieldAnnotationRow}>
        <span className={styles.fieldAnnotationLabel}>Source</span>
        <span>{source === 'user' ? 'Operator-edited' : 'AI-generated'}</span>
      </span>
      <span className={styles.fieldAnnotationRow}>
        <span className={styles.fieldAnnotationLabel}>What</span>
        <span>{what}</span>
      </span>
    </HoverPopover>
  );
}

/** contentEditable rich-text area with a small toolbar, shown only while
 * editing. Uncontrolled: seeded + focused once on mount (edit start), read on
 * input; the server sanitizes the HTML on save. */
function RichTextArea({
  seedHtml,
  onChange,
}: {
  seedHtml: string;
  onChange: (html: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = seedHtml;
      ref.current.focus();
    }
    // Seed once on mount (entering edit mode); typing is uncontrolled after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command: string) => {
    document.execCommand(command, false);
    ref.current?.focus();
    onChange(ref.current?.innerHTML ?? '');
  };

  return (
    <div className={styles.ruEditArea}>
      <div className={styles.ruToolbar}>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>
          <strong>B</strong>
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>
          <em>I</em>
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}
        >
          • List
        </button>
      </div>
      <div
        ref={ref}
        className={styles.ruEditor}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
      />
    </div>
  );
}

interface RecentUpdatesRowProps {
  label: string;
  what: string;
  html: string;
  source: 'user' | 'ai';
  suppressed: boolean;
  history?: EditHistoryEntry[];
  disabled: boolean;
  canSeeWhy: boolean;
  whyHref: string;
  onSave: (html: string) => Promise<void>;
  onSuppress: (suppressed: boolean) => Promise<void>;
}

/** One Recent Updates block rendered as a FieldRow: read-only by default
 * (rendered HTML + ⓘ), green/blue change bar, edit ✎ / dismiss ✕ / why ?
 * cluster; the rich-text toolbar appears only while editing. A dismissed block
 * shows the muted dismissed-row treatment + restore ↺. */
function RecentUpdatesRow({
  label, what, html, source, suppressed, history, disabled,
  canSeeWhy, whyHref, onSave, onSuppress,
}: RecentUpdatesRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(html);
  const [saving, setSaving] = useState(false);

  // Re-sync from the server ONLY when not editing, so a background aggregation
  // poll can't wipe an in-progress edit (save-time 409 handles true staleness).
  useEffect(() => {
    if (!editing) setDraft(html);
  }, [html, editing]);

  // Dismissed → muted row + restore, matching a dismissed FieldRow.
  if (suppressed && !editing) {
    return (
      <div className={`${styles.fieldRow} ${styles.fieldRowFull} ${styles.fieldRowDismissed}`}>
        <div className={styles.fieldLabel}>
          <span className={styles.fieldLabelText}>{label}</span>
          <span className={styles.fieldDismissedStatus}>Dismissed — not logged</span>
        </div>
        <div className={styles.fieldValue}>
          {html ? (
            <div className={styles.ruReadonly} dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <span className={styles.ruEmpty}>—</span>
          )}
        </div>
        <div className={styles.fieldActions}>
          <button
            type="button"
            className={styles.fieldActionBtn}
            disabled={disabled}
            title={`Restore ${label}`}
            aria-label={`Restore ${label}`}
            onClick={() => { onSuppress(false).catch(() => {}); }}
          >
            ↺
          </button>
        </div>
      </div>
    );
  }

  if (!editing) {
    const barClass =
      source === 'user' ? styles.fieldUserSet : html ? styles.fieldRowChangeAdd : '';
    return (
      <div className={[styles.fieldRow, styles.fieldRowFull, barClass].filter(Boolean).join(' ')}>
        <div className={styles.fieldLabel}>
          <span className={styles.fieldLabelText}>{label}</span>
          <RecentUpdatesInfoTooltip source={source} what={what} />
          {source === 'user' && (
            <EditHistoryTooltip
              history={history}
              label={<span className={styles.fieldStatusBadge}>· edited</span>}
            />
          )}
        </div>
        <div className={styles.fieldValue}>
          {html ? (
            // Server-sanitized (override) or escaped AI bullets — safe to render.
            <div className={styles.ruReadonly} dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <span className={styles.ruEmpty}>—</span>
          )}
        </div>
        <div className={styles.fieldActions}>
          <button
            type="button"
            className={styles.fieldActionBtn}
            disabled={disabled}
            title={`Edit ${label}`}
            aria-label={`Edit ${label}`}
            onClick={() => { setDraft(html); setEditing(true); }}
          >
            ✎
          </button>
          <button
            type="button"
            className={styles.fieldActionBtn}
            disabled={disabled}
            title={`Dismiss ${label} (don't log this block)`}
            aria-label={`Dismiss ${label}`}
            onClick={() => { onSuppress(true).catch(() => {}); }}
          >
            ×
          </button>
          {canSeeWhy && (
            <a
              href={whyHref}
              className={styles.fieldActionBtn}
              title="Why? — open in Pipeline Insights"
              style={{ textDecoration: 'none' }}
            >
              ?
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.fieldRow} ${styles.fieldRowFull} ${styles.fieldEditing}`}>
      <div className={styles.fieldLabel}>{label}</div>
      <RichTextArea seedHtml={draft} onChange={setDraft} />
      <div className={styles.editActions}>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(draft);
              setEditing(false);
            } catch {
              /* error surfaced by the parent banner; stay in edit mode */
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => { setDraft(html); setEditing(false); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

interface RecentUpdatesProps {
  dealKey: string;
  dealKind: 'candidate' | 'affinity_opp';
  recentUpdates: RecentUpdates;
  reviewSuggestionId: number | null;
  canSeeWhy: boolean;
}

const RU_WHAT: Record<'progress' | 'new_info', string> = {
  progress: 'Process / milestone events since the last log.',
  new_info: 'Newly-learned facts material to the deal.',
};

/** The two Recent Updates rows (Progress + New Info). Editing one composes the
 * combined {progress,new_info} html payload preserving the OTHER block's source
 * (user override kept, AI block stays AI). Dismiss/restore is a separate
 * suppress-only mutation that never rebuilds html. */
function RecentUpdatesEditor({
  dealKey, dealKind, recentUpdates, reviewSuggestionId, canSeeWhy,
}: RecentUpdatesProps) {
  const setMut = useSetDealRecentUpdates();
  const suppressMut = useSuppressRecentUpdatesBlock();
  const [error, setError] = useState<string | null>(null);

  const keepOther = (src: 'user' | 'ai', otherHtml: string) =>
    src === 'user' ? otherHtml : null;

  const errFor = (e: unknown) => {
    const conflict = e instanceof ApiError && e.status === 409;
    setError(
      conflict
        ? 'This card updated since you started editing. Copy your text, refresh, and re-apply.'
        : 'Could not save Recent Updates. Try again.',
    );
  };

  const saveBlock = (block: 'progress' | 'new_info', html: string) =>
    new Promise<void>((resolve, reject) => {
      setError(null);
      const progressHtml =
        block === 'progress'
          ? html
          : keepOther(recentUpdates.progress_source, recentUpdates.progress_html);
      const newInfoHtml =
        block === 'new_info'
          ? html
          : keepOther(recentUpdates.new_info_source, recentUpdates.new_info_html);
      setMut.mutate(
        { dealKey, progressHtml, newInfoHtml, reviewSuggestionId },
        { onSuccess: () => resolve(), onError: (e) => { errFor(e); reject(e); } },
      );
    });

  const suppressBlock = (block: 'progress' | 'new_info', suppressed: boolean) =>
    new Promise<void>((resolve, reject) => {
      setError(null);
      suppressMut.mutate(
        { dealKey, block, suppressed, reviewSuggestionId },
        { onSuccess: () => resolve(), onError: (e) => { errFor(e); reject(e); } },
      );
    });

  const whyHref =
    `/?tab=pipeline-insights&deal_kind=${dealKind}`
    + `&deal_key=${encodeURIComponent(dealKey)}`;
  const disabled = setMut.isPending || suppressMut.isPending;

  return (
    <>
      <RecentUpdatesRow
        label="Progress"
        what={RU_WHAT.progress}
        html={recentUpdates.progress_html}
        source={recentUpdates.progress_source}
        suppressed={recentUpdates.progress_suppressed}
        history={recentUpdates.progress_history}
        disabled={disabled}
        canSeeWhy={canSeeWhy}
        whyHref={whyHref}
        onSave={(h) => saveBlock('progress', h)}
        onSuppress={(s) => suppressBlock('progress', s)}
      />
      <RecentUpdatesRow
        label="New Info"
        what={RU_WHAT.new_info}
        html={recentUpdates.new_info_html}
        source={recentUpdates.new_info_source}
        suppressed={recentUpdates.new_info_suppressed}
        history={recentUpdates.new_info_history}
        disabled={disabled}
        canSeeWhy={canSeeWhy}
        whyHref={whyHref}
        onSave={(h) => saveBlock('new_info', h)}
        onSuppress={(s) => suppressBlock('new_info', s)}
      />
      {error && <div className={styles.ruError}>{error}</div>}
    </>
  );
}
